/**
 * Root console: Paymod monitoring. Reachable by the root user only, so its UI text is
 * hardcoded English (not i18n) by design. Do not introduce translation keys here.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Select, TextInput } from "flowbite-react";
import { AlertMessage } from "../components/atoms/AlertMessage";
import { Spinner } from "../components/atoms/Spinner";
import {
  HiCash,
  HiRefresh,
  HiSearch,
  HiX,
  HiSortAscending,
  HiSortDescending,
} from "react-icons/hi";
import useAuth from "../hooks/useAuth";
import { Copy } from "../components/atoms/Copy";
import { CustomButton } from "../components/atoms/CustomButton";
import { DataTable } from "../components/atoms/DataTable";
import { EmptyState } from "../components/atoms/EmptyState";
import PaymentModuleService, {
  PayoutResponseDTO,
  PayoutStatusEnum,
  PayoutMethodEnum,
  PayoutAccountResponseDTO,
  PayoutStatusUpdateDTO,
  PayoutMethod,
  PayoutStatus,
} from "../services/paymentModule";
import { toast } from "react-toastify";
import PayoutDrawer from "../components/pages/dashboard/paymod/PayoutDrawer";
import Pagination from "../components/public/Pagination";
import {
  formatAmount,
  formatDate,
  formatDateTime,
  getMethodBadge,
  getStatusBadge,
} from "../components/pages/dashboard/paymod/payoutHelpers";

type SearchType = "payment_step" | "payment" | "payout" | "entity";

interface SearchResult {
  type: SearchType;
  singlePayout?: PayoutResponseDTO;
  multiplePayouts?: PayoutResponseDTO[];
}

const Paymod: React.FC = () => {
  const navigate = useNavigate();
  const { customer_identification, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payouts, setPayouts] = useState<PayoutResponseDTO[]>([]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filterStatus, setFilterStatus] = useState<PayoutStatusEnum | "">("");
  const [filterMethod, setFilterMethod] = useState<PayoutMethodEnum | "">("");

  const [sortBy, setSortBy] = useState<"netAmount" | "creationDateTime">(
    "creationDateTime"
  );
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const [searchType, setSearchType] = useState<SearchType>("payment_step");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);

  const [selectedPayout, setSelectedPayout] =
    useState<PayoutResponseDTO | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerResults, setDrawerResults] = useState<
    PayoutResponseDTO[] | null
  >(null);
  const [drawerResultsLoading, setDrawerResultsLoading] = useState(false);

  const [accountInfo, setAccountInfo] =
    useState<PayoutAccountResponseDTO | null>(null);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountRequested, setAccountRequested] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [selectedPayoutForStatus, setSelectedPayoutForStatus] =
    useState<PayoutResponseDTO | null>(null);
  const [newStatus, setNewStatus] = useState<PayoutStatusEnum>("PENDING");
  const [providerReference, setProviderReference] = useState("");

  const searchTypeOptions = [
    {
      value: "payment_step",
      label: "Payment step ID",
      placeholder: "Enter a payment step ID",
      description: "Find the payout containing a specific payment step",
    },
    {
      value: "payment",
      label: "Payment ID",
      placeholder: "Enter a payment ID",
      description: "Find all payouts related to a payment",
    },
    {
      value: "payout",
      label: "Payout ID",
      placeholder: "Enter a payout ID",
      description: "View payout details and its payment steps",
    },
    {
      value: "entity",
      label: "Entity ID",
      placeholder: "Enter Entity ID",
      description: "Find all payouts for a specific entity",
    },
  ];

  const currentSearchOption = searchTypeOptions.find(
    (opt) => opt.value === searchType
  );

  const fetchAllPayouts = async (isRefresh = false, resetPage = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const currentPage = resetPage ? 0 : page;
      if (resetPage) setPage(0);

      const response = await PaymentModuleService.getAllPayouts(
        customer_identification,
        user.entity_identification,
        {
          page: currentPage,
          size: pageSize,
          status: filterStatus || undefined,
          method: filterMethod || undefined,
          sortBy,
          sortDir,
        }
      );

      const paginatedData = response.data?.element;
      if (paginatedData) {
        setPayouts(paginatedData.content || []);
        setTotalElements(paginatedData.total_elements);
        setTotalPages(paginatedData.total_pages);
      } else {
        setPayouts([]);
        setTotalElements(0);
        setTotalPages(0);
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to fetch payouts";
      setError(errorMessage);
      if (isRefresh) {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllPayouts();
  }, [page, pageSize]);

  useEffect(() => {
    fetchAllPayouts(false, true);
  }, [filterStatus, filterMethod, sortBy, sortDir]);

  useEffect(() => {
    if (
      searchResult &&
      (searchResult.type === "payment_step" ||
        searchResult.type === "payout") &&
      searchResult.singlePayout
    ) {
      openPayoutDrawer(searchResult.singlePayout);
    }
  }, [searchResult]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error(`Please enter a ${currentSearchOption?.label ?? ""}`);
      return;
    }

    try {
      setSearchLoading(true);
      const query = searchQuery.trim();

      switch (searchType) {
        case "payment_step": {
          const response = await PaymentModuleService.getPayoutByPaymentStep(
            customer_identification,
            user.entity_identification,
            query
          );
          setSearchResult({
            type: "payment_step",
            singlePayout: response.data?.element || undefined,
          });
          break;
        }
        case "payment": {
          const response = await PaymentModuleService.getPayoutsByPayment(
            customer_identification,
            user.entity_identification,
            query
          );
          setSearchResult({
            type: "payment",
            multiplePayouts: response.data?.element || [],
          });
          break;
        }
        case "payout": {
          const response = await PaymentModuleService.getPayoutDetails(
            customer_identification,
            user.entity_identification,
            query
          );
          setSearchResult({
            type: "payout",
            singlePayout: response.data?.element || undefined,
          });
          break;
        }
        case "entity": {
          const response = await PaymentModuleService.getPayoutsByEntity(
            customer_identification,
            user.entity_identification,
            query
          );
          setSearchResult({
            type: "entity",
            multiplePayouts: response.data?.element || [],
          });
          break;
        }
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Search failed";
      toast.error(errorMessage);
      setSearchResult(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResult(null);
  };

  const handleSearchById = async (type: SearchType, id: string) => {
    setSearchType(type);
    setSearchQuery(id);
    setSearchResult(null);

    try {
      setSearchLoading(true);
      let response;

      if (type === "payment_step") {
        response = await PaymentModuleService.getPayoutByPaymentStep(
          customer_identification,
          user.entity_identification,
          id
        );
        setSearchResult({
          type: "payment_step",
          singlePayout: response.data?.element || undefined,
        });
      } else if (type === "payment") {
        response = await PaymentModuleService.getPayoutsByPayment(
          customer_identification,
          user.entity_identification,
          id
        );
        setSearchResult({
          type: "payment",
          multiplePayouts: response.data?.element || [],
        });
      } else if (type === "payout") {
        response = await PaymentModuleService.getPayoutDetails(
          customer_identification,
          user.entity_identification,
          id
        );
        setSearchResult({
          type: "payout",
          singlePayout: response.data?.element || undefined,
        });
      } else if (type === "entity") {
        response = await PaymentModuleService.getPayoutsByEntity(
          customer_identification,
          user.entity_identification,
          id
        );
        setSearchResult({
          type: "entity",
          multiplePayouts: response.data?.element || [],
        });
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || "Search failed";
      toast.error(errorMessage);
      setSearchResult(null);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleViewAccount = async (payout: PayoutResponseDTO) => {
    setAccountRequested(true);
    setAccountLoading(true);
    setAccountInfo(null);

    try {
      const response = await PaymentModuleService.getPayoutAccount(
        customer_identification,
        user.entity_identification,
        payout.payout_identification
      );
      setAccountInfo(response.data?.element || null);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch account information";
      toast.error(errorMessage);
    } finally {
      setAccountLoading(false);
    }
  };

  const openPayoutDrawer = (payout: PayoutResponseDTO) => {
    setSelectedPayout(payout);
    setDrawerResults(null);
    setAccountInfo(null);
    setAccountRequested(false);
    setAccountLoading(false);
    setDrawerOpen(true);
  };

  const closePayoutDrawer = () => {
    setDrawerOpen(false);
    setDrawerResults(null);
  };

  const handleDrawerSearchById = async (type: SearchType, id: string) => {
    setDrawerResultsLoading(true);
    try {
      let payouts: PayoutResponseDTO[] = [];
      if (type === "payment_step") {
        const res = await PaymentModuleService.getPayoutByPaymentStep(
          customer_identification,
          user.entity_identification,
          id
        );
        if (res.data?.element) payouts = [res.data.element];
      } else if (type === "payout") {
        const res = await PaymentModuleService.getPayoutDetails(
          customer_identification,
          user.entity_identification,
          id
        );
        if (res.data?.element) payouts = [res.data.element];
      } else if (type === "payment") {
        const res = await PaymentModuleService.getPayoutsByPayment(
          customer_identification,
          user.entity_identification,
          id
        );
        payouts = res.data?.element || [];
      } else if (type === "entity") {
        const res = await PaymentModuleService.getPayoutsByEntity(
          customer_identification,
          user.entity_identification,
          id
        );
        payouts = res.data?.element || [];
      }

      if (payouts.length === 1) {
        openPayoutDrawer(payouts[0]);
      } else if (payouts.length > 1) {
        setDrawerResults(payouts);
      } else {
        toast.info("No payout found for that reference.");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to search for the payout"
      );
    } finally {
      setDrawerResultsLoading(false);
    }
  };

  const handleOpenStatusUpdate = (payout: PayoutResponseDTO) => {
    setSelectedPayoutForStatus(payout);
    setNewStatus(payout.status);
    setProviderReference(payout.provider_reference || "");
    setShowStatusModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedPayoutForStatus) return;

    try {
      setStatusUpdateLoading(true);
      const updateData: PayoutStatusUpdateDTO = {
        status: newStatus,
        provider_reference: providerReference || undefined,
      };

      await PaymentModuleService.updatePayoutStatus(
        customer_identification,
        user.entity_identification,
        selectedPayoutForStatus.payout_identification,
        updateData
      );

      toast.success("Payout status updated");
      setShowStatusModal(false);
      setSelectedPayoutForStatus(null);

      if (
        selectedPayout?.payout_identification ===
        selectedPayoutForStatus.payout_identification
      ) {
        try {
          const refreshed = await PaymentModuleService.getPayoutDetails(
            customer_identification,
            user.entity_identification,
            selectedPayoutForStatus.payout_identification
          );
          if (refreshed.data?.element) {
            setSelectedPayout(refreshed.data.element);
          }
        } catch {}
      }

      fetchAllPayouts(true);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to update the status";
      toast.error(errorMessage);
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const renderSearchResults = () => {
    if (!searchResult) return null;

    switch (searchResult.type) {
      case "payment_step":
      case "payout":
        if (!searchResult.singlePayout) {
          return (
            <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center text-sm text-gray-500">
              {searchResult.type === "payment_step"
                ? "No payout found for this payment step"
                : "Payout not found"}
            </p>
          );
        }
        return null;

      case "payment":
      case "entity":
        if (
          !searchResult.multiplePayouts ||
          searchResult.multiplePayouts.length === 0
        ) {
          return (
            <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center text-sm text-gray-500">
              {searchResult.type === "entity"
                ? "No payouts found for this entity"
                : "No payouts found for this payment"}
            </p>
          );
        }
        return (
          <div>
            <p className="mb-2 text-xs text-gray-500 tabular-nums">
              {searchResult.multiplePayouts.length} payouts found
            </p>
            <DataTable>
              <DataTable.Head>
                <DataTable.HeadCell>Payout ID</DataTable.HeadCell>
                <DataTable.HeadCell>Status</DataTable.HeadCell>
                <DataTable.HeadCell>Method</DataTable.HeadCell>
                <DataTable.HeadCell numeric>Net amount</DataTable.HeadCell>
                <DataTable.HeadCell>Period</DataTable.HeadCell>
              </DataTable.Head>
              <DataTable.Body>
                {searchResult.multiplePayouts.map((p) => (
                  <DataTable.Row
                    key={p.payout_identification}
                    className="cursor-pointer"
                    onClick={() => openPayoutDrawer(p)}
                  >
                    <DataTable.Cell className="font-mono text-xs">
                      <div className="flex items-center gap-1">
                        <span onClick={(e) => e.stopPropagation()}>
                          <Copy target={p.payout_identification} truncate />
                        </span>
                        <CustomButton
                          variant="none"
                          size="icon"
                          className="text-gray-400 enabled:hover:text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSearchById("payout", p.payout_identification);
                          }}
                          tooltip="Search by this payout ID"
                          icon={<HiSearch size={14} />}
                        />
                      </div>
                    </DataTable.Cell>
                    <DataTable.Cell>{getStatusBadge(p.status)}</DataTable.Cell>
                    <DataTable.Cell>
                      {getMethodBadge(p.payout_method)}
                    </DataTable.Cell>
                    <DataTable.Cell
                      numeric
                      className="whitespace-nowrap font-medium text-gray-900"
                    >
                      {formatAmount(p.net_amount, p.currency)}
                    </DataTable.Cell>
                    <DataTable.Cell className="whitespace-nowrap tabular-nums">
                      {formatDate(p.period_start)} - {formatDate(p.period_end)}
                    </DataTable.Cell>
                  </DataTable.Row>
                ))}
              </DataTable.Body>
            </DataTable>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex justify-center items-center h-64">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="paymod-page">
      <Helmet>
        <title>Paymod monitoring | Super User Manager</title>
        <meta
          name="description"
          content="Payments module monitoring dashboard"
        />
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">
              Paymod monitoring
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Monitor payouts and payment steps
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CustomButton
              onClick={() => fetchAllPayouts(true)}
              disabled={refreshing}
              variant="secondary"
              size="sm"
              comp={
                <span className="flex items-center gap-2">
                  <HiRefresh
                    className={refreshing ? "animate-spin" : ""}
                    size={16}
                  />
                  {refreshing ? "Refreshing..." : "Refresh"}
                </span>
              }
            />
          </div>
        </div>

        {error && (
          <AlertMessage color="failure" className="mb-6">
            <span className="font-medium">Error!</span> {error}
          </AlertMessage>
        )}

        <div className="grid grid-cols-1 gap-6">
          <section className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-lg font-semibold text-gray-900">Search</h2>
              <p className="mt-0.5 text-sm text-gray-500">
                {currentSearchOption?.description}
              </p>
            </div>

            <div className="flex flex-col gap-3 p-4 sm:flex-row">
              <div className="w-full sm:w-48">
                <Select
                  value={searchType}
                  onChange={(e) => {
                    setSearchType(e.target.value as SearchType);
                    setSearchResult(null);
                    setSearchQuery("");
                  }}
                >
                  {searchTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex-1">
                <TextInput
                  type="text"
                  placeholder={currentSearchOption?.placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </div>
              <CustomButton
                onClick={handleSearch}
                disabled={searchLoading}
                variant="primary"
                icon="search"
                title={searchLoading ? "Searching..." : "Search"}
              />
              {searchResult && (
                <CustomButton
                  onClick={handleClearSearch}
                  variant="secondary"
                  title="Clear"
                />
              )}
            </div>

            <div className="px-4 pb-4">{renderSearchResults()}</div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  All payouts
                </h2>
                <p className="mt-0.5 text-sm tabular-nums text-gray-500">
                  {totalElements} payouts in the system
                </p>
              </div>
              {(filterStatus || filterMethod) && (
                <CustomButton
                  onClick={() => {
                    setFilterStatus("");
                    setFilterMethod("");
                  }}
                  variant="secondary"
                  size="sm"
                  comp={
                    <span className="flex items-center gap-1.5">
                      <HiX size={14} />
                      Clear filters
                    </span>
                  }
                />
              )}
            </div>

            <div className="flex flex-col gap-4 px-4 pt-4 lg:flex-row">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="w-full sm:w-40">
                  <label className="block text-xs text-gray-500 mb-1">
                    Status
                  </label>
                  <Select
                    value={filterStatus}
                    onChange={(e) =>
                      setFilterStatus(e.target.value as PayoutStatusEnum | "")
                    }
                    sizing="sm"
                  >
                    <option value="">All statuses</option>
                    <option value={PayoutStatus.PENDING}>Pending</option>
                    <option value={PayoutStatus.PROCESSING}>Processing</option>
                    <option value={PayoutStatus.PAID}>Paid</option>
                    <option value={PayoutStatus.FAILED}>Failed</option>
                  </Select>
                </div>
                <div className="w-full sm:w-48">
                  <label className="block text-xs text-gray-500 mb-1">
                    Method
                  </label>
                  <Select
                    value={filterMethod}
                    onChange={(e) =>
                      setFilterMethod(e.target.value as PayoutMethodEnum | "")
                    }
                    sizing="sm"
                  >
                    <option value="">All methods</option>
                    <option value={PayoutMethod.BANK_TRANSFER}>
                      Bank transfer
                    </option>
                    <option value={PayoutMethod.CRYPTO}>Crypto</option>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <div className="w-full sm:w-40">
                  <label className="block text-xs text-gray-500 mb-1">
                    Sort by
                  </label>
                  <Select
                    value={sortBy}
                    onChange={(e) =>
                      setSortBy(
                        e.target.value as "netAmount" | "creationDateTime"
                      )
                    }
                    sizing="sm"
                  >
                    <option value="creationDateTime">Created</option>
                    <option value="netAmount">Net amount</option>
                  </Select>
                </div>
                <div className="w-full sm:w-32">
                  <label className="block text-xs text-gray-500 mb-1">
                    Order
                  </label>
                  <CustomButton
                    onClick={() =>
                      setSortDir(sortDir === "asc" ? "desc" : "asc")
                    }
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    comp={
                      <span className="flex items-center gap-2 text-sm">
                        {sortDir === "asc" ? (
                          <>
                            <HiSortAscending size={16} />
                            Ascending
                          </>
                        ) : (
                          <>
                            <HiSortDescending size={16} />
                            Descending
                          </>
                        )}
                      </span>
                    }
                  />
                </div>
              </div>

              <div className="w-full sm:w-28">
                <label className="block text-xs text-gray-500 mb-1">
                  Per page
                </label>
                <Select
                  value={pageSize.toString()}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  sizing="sm"
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </Select>
              </div>
            </div>

            <div className="mt-4">
              <DataTable wrapperClassName="rounded-none border-x-0 border-b-0">
                <DataTable.Head>
                  <DataTable.HeadCell>Created</DataTable.HeadCell>
                  <DataTable.HeadCell>Payout ID</DataTable.HeadCell>
                  <DataTable.HeadCell>Entity ID</DataTable.HeadCell>
                  <DataTable.HeadCell>Status</DataTable.HeadCell>
                  <DataTable.HeadCell>Method</DataTable.HeadCell>
                  <DataTable.HeadCell numeric>Net amount</DataTable.HeadCell>
                  <DataTable.HeadCell>Period</DataTable.HeadCell>
                </DataTable.Head>
                <DataTable.Body>
                  {refreshing ? (
                    <DataTable.Row>
                      <DataTable.Cell colSpan={7} className="py-8 text-center">
                        <Spinner size="lg" />
                      </DataTable.Cell>
                    </DataTable.Row>
                  ) : payouts.length === 0 ? (
                    <DataTable.Row>
                      <DataTable.Cell colSpan={7}>
                        <EmptyState
                          icon={<HiCash size={24} />}
                          title="No payouts found"
                        />
                      </DataTable.Cell>
                    </DataTable.Row>
                  ) : (
                    payouts.map((payout) => (
                      <DataTable.Row
                        key={payout.payout_identification}
                        className="cursor-pointer"
                        onClick={() => openPayoutDrawer(payout)}
                      >
                        <DataTable.Cell className="whitespace-nowrap tabular-nums">
                          {formatDateTime(payout.creation_date_time)}
                        </DataTable.Cell>
                        <DataTable.Cell className="font-mono text-xs">
                          <div className="flex items-center gap-1">
                            <span onClick={(e) => e.stopPropagation()}>
                              <Copy
                                target={payout.payout_identification}
                                truncate
                              />
                            </span>
                            <CustomButton
                              variant="none"
                              size="icon"
                              className="text-gray-400 enabled:hover:text-blue-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSearchById(
                                  "payout",
                                  payout.payout_identification
                                );
                              }}
                              tooltip="Search by this payout ID"
                              icon={<HiSearch size={14} />}
                            />
                          </div>
                        </DataTable.Cell>
                        <DataTable.Cell className="font-mono text-xs">
                          <div className="flex items-center gap-1">
                            <span onClick={(e) => e.stopPropagation()}>
                              <Copy
                                target={payout.entity_identification}
                                truncate
                              />
                            </span>
                            <CustomButton
                              variant="none"
                              size="icon"
                              className="text-gray-400 enabled:hover:text-blue-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(
                                  `/control-panel/monitoring?entityId=${payout.entity_identification}`
                                );
                              }}
                              tooltip="Find this entity in Entities monitoring"
                              icon={<HiSearch size={14} />}
                            />
                          </div>
                        </DataTable.Cell>
                        <DataTable.Cell>
                          {getStatusBadge(payout.status)}
                        </DataTable.Cell>
                        <DataTable.Cell>
                          {getMethodBadge(payout.payout_method)}
                        </DataTable.Cell>
                        <DataTable.Cell
                          numeric
                          className="whitespace-nowrap font-medium text-gray-900"
                        >
                          {formatAmount(payout.net_amount, payout.currency)}
                        </DataTable.Cell>
                        <DataTable.Cell className="whitespace-nowrap tabular-nums">
                          {formatDate(payout.period_start)} -{" "}
                          {formatDate(payout.period_end)}
                        </DataTable.Cell>
                      </DataTable.Row>
                    ))
                  )}
                </DataTable.Body>
              </DataTable>
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row">
                <p className="text-xs text-gray-500 tabular-nums">
                  Showing {page * pageSize + 1} to{" "}
                  {Math.min((page + 1) * pageSize, totalElements)} of{" "}
                  {totalElements} payouts
                </p>
                <Pagination
                  pageCount={totalPages}
                  currentPage={page}
                  onPageChange={(selected) => setPage(selected)}
                />
              </div>
            )}
          </section>
        </div>
      </div>

      <PayoutDrawer
        open={drawerOpen}
        onClose={closePayoutDrawer}
        payout={selectedPayout}
        results={drawerResults}
        resultsLoading={drawerResultsLoading}
        onSelectResult={openPayoutDrawer}
        onSearchById={handleDrawerSearchById}
        accountInfo={accountInfo}
        accountLoading={accountLoading}
        accountRequested={accountRequested}
        onViewAccount={handleViewAccount}
        showStatusModal={showStatusModal}
        onOpenStatusUpdate={handleOpenStatusUpdate}
        onCloseStatusModal={() => setShowStatusModal(false)}
        statusUpdateLoading={statusUpdateLoading}
        selectedPayoutForStatus={selectedPayoutForStatus}
        newStatus={newStatus}
        onNewStatusChange={setNewStatus}
        providerReference={providerReference}
        onProviderReferenceChange={setProviderReference}
        onUpdateStatus={handleUpdateStatus}
      />
    </div>
  );
};

export default Paymod;
